import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import useSWR from 'swr'
import { Badge, Banner, Button, FormattedDateTime, ImageSlot, Panel, SlugManager, ToggleGroup, TrashIcon, useSpeedbump } from '../components'
import MarkdownEditor from '../components/MarkdownEditor/MarkdownEditor'
import VideoEmbedEditor, { type VideoEmbedMetadata } from '../components/merchandising/metadata-editors/VideoEmbedEditor'
import { JWPLAYER_DEFAULT_PLAYER_ID } from '../constants/jwplayer'
import { useDragReorder } from '../hooks'
import { useToast } from '../contexts/ToastContext'
import {
  fetchArticle,
  updateArticle,
  uploadArticleImage,
  type ArticleDetail as ArticleDetailType,
  type ArticlePart,
  type ArticleStage,
  type ExternalPart,
  type MarkdownPart,
  type PhotoImageMetadataUpdate,
  type PhotoPart,
  type ResolvedImage,
  type VideoEmbedPart,
} from '../services/articlesApi'
import { arrayMove } from '../utils/arrayMove'
import formStyles from '../styles/forms.module.scss'
import pageStyles from '../styles/page.module.scss'
import styles from './ArticleDetail.module.scss'

const STAGE_OPTIONS: { value: ArticleStage; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
]

const escapeHtmlAttr = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * Templates mirror what each provider's own "Copy embed code" button emits, so
 * the pre-script-load placeholder has the same width / loading-state styling
 * users see on the source site. The provider's widget script (loaded once per
 * page by the article route) finds the blockquote and replaces it with the
 * live iframe — the inner skeleton is purely cosmetic until then.
 */
const buildInstagramHtml = (permalinkUrl: string): string => {
  const safe = escapeHtmlAttr(permalinkUrl)
  return `<blockquote class="instagram-media" data-instgrm-captioned data-instgrm-permalink="${safe}" data-instgrm-version="14" style=" background:#FFF; border:0; border-radius:3px; box-shadow:0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15); margin: 1px; max-width:658px; min-width:326px; padding:0; width:99.375%; width:-webkit-calc(100% - 2px); width:calc(100% - 2px);"><div style="padding:16px;"> <a href="${safe}" style=" background:#FFFFFF; line-height:0; padding:0 0; text-align:center; text-decoration:none; width:100%;" target="_blank"> <div style=" display: flex; flex-direction: row; align-items: center;"> <div style="background-color: #F4F4F4; border-radius: 50%; flex-grow: 0; height: 40px; margin-right: 14px; width: 40px;"></div> <div style="display: flex; flex-direction: column; flex-grow: 1; justify-content: center;"> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; margin-bottom: 6px; width: 100px;"></div> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; width: 60px;"></div></div></div><div style="padding: 19% 0;"></div> <div style="display:block; height:50px; margin:0 auto 12px; width:50px;"><svg width="50px" height="50px" viewBox="0 0 60 60" version="1.1" xmlns="https://www.w3.org/2000/svg" xmlns:xlink="https://www.w3.org/1999/xlink"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-511.000000, -20.000000)" fill="#000000"><g><path d="M556.869,30.41 C554.814,30.41 553.148,32.076 553.148,34.131 C553.148,36.186 554.814,37.852 556.869,37.852 C558.924,37.852 560.59,36.186 560.59,34.131 C560.59,32.076 558.924,30.41 556.869,30.41 M541,60.657 C535.114,60.657 530.342,55.887 530.342,50 C530.342,44.114 535.114,39.342 541,39.342 C546.887,39.342 551.658,44.114 551.658,50 C551.658,55.887 546.887,60.657 541,60.657 M541,33.886 C532.1,33.886 524.886,41.1 524.886,50 C524.886,58.899 532.1,66.113 541,66.113 C549.9,66.113 557.115,58.899 557.115,50 C557.115,41.1 549.9,33.886 541,33.886 M565.378,62.101 C565.244,65.022 564.756,66.606 564.346,67.663 C563.803,69.06 563.154,70.057 562.106,71.106 C561.058,72.155 560.06,72.803 558.662,73.347 C557.607,73.757 556.021,74.244 553.102,74.378 C549.944,74.521 548.997,74.552 541,74.552 C533.003,74.552 532.056,74.521 528.898,74.378 C525.979,74.244 524.393,73.757 523.338,73.347 C521.94,72.803 520.942,72.155 519.894,71.106 C518.846,70.057 518.197,69.06 517.654,67.663 C517.244,66.606 516.755,65.022 516.623,62.101 C516.479,58.943 516.448,57.996 516.448,50 C516.448,42.003 516.479,41.056 516.623,37.899 C516.755,34.978 517.244,33.391 517.654,32.338 C518.197,30.938 518.846,29.942 519.894,28.894 C520.942,27.846 521.94,27.196 523.338,26.654 C524.393,26.244 525.979,25.756 528.898,25.623 C532.057,25.479 533.004,25.448 541,25.448 C548.997,25.448 549.943,25.479 553.102,25.623 C556.021,25.756 557.607,26.244 558.662,26.654 C560.06,27.196 561.058,27.846 562.106,28.894 C563.154,29.942 563.803,30.938 564.346,32.338 C564.756,33.391 565.244,34.978 565.378,37.899 C565.522,41.056 565.552,42.003 565.552,50 C565.552,57.996 565.522,58.943 565.378,62.101 M570.82,37.631 C570.674,34.438 570.167,32.258 569.425,30.349 C568.659,28.377 567.633,26.702 565.965,25.035 C564.297,23.368 562.623,22.342 560.652,21.575 C558.743,20.834 556.562,20.326 553.369,20.18 C550.169,20.033 549.148,20 541,20 C532.853,20 531.831,20.033 528.631,20.18 C525.438,20.326 523.257,20.834 521.349,21.575 C519.376,22.342 517.703,23.368 516.035,25.035 C514.368,26.702 513.342,28.377 512.574,30.349 C511.834,32.258 511.326,34.438 511.181,37.631 C511.035,40.831 511,41.851 511,50 C511,58.147 511.035,59.17 511.181,62.369 C511.326,65.562 511.834,67.743 512.574,69.651 C513.342,71.625 514.368,73.296 516.035,74.965 C517.703,76.634 519.376,77.658 521.349,78.425 C523.257,79.167 525.438,79.673 528.631,79.82 C531.831,79.965 532.853,80.001 541,80.001 C549.148,80.001 550.169,79.965 553.369,79.82 C556.562,79.673 558.743,79.167 560.652,78.425 C562.623,77.658 564.297,76.634 565.965,74.965 C567.633,73.296 568.659,71.625 569.425,69.651 C570.167,67.743 570.674,65.562 570.82,62.369 C570.966,59.17 571,58.147 571,50 C571,41.851 570.966,40.831 570.82,37.631"></path></g></g></g></svg></div><div style="padding-top: 8px;"> <div style=" color:#3897f0; font-family:Arial,sans-serif; font-size:14px; font-style:normal; font-weight:550; line-height:18px;">View this post on Instagram</div></div><div style="padding: 12.5% 0;"></div> <div style="display: flex; flex-direction: row; margin-bottom: 14px; align-items: center;"><div> <div style="background-color: #F4F4F4; border-radius: 50%; height: 12.5px; width: 12.5px; transform: translateX(0px) translateY(7px);"></div> <div style="background-color: #F4F4F4; height: 12.5px; transform: rotate(-45deg) translateX(3px) translateY(1px); width: 12.5px; flex-grow: 0; margin-right: 14px; margin-left: 2px;"></div> <div style="background-color: #F4F4F4; border-radius: 50%; height: 12.5px; width: 12.5px; transform: translateX(9px) translateY(-18px);"></div></div><div style="margin-left: 8px;"> <div style=" background-color: #F4F4F4; border-radius: 50%; flex-grow: 0; height: 20px; width: 20px;"></div> <div style=" width: 0; height: 0; border-top: 2px solid transparent; border-left: 6px solid #f4f4f4; border-bottom: 2px solid transparent; transform: translateX(16px) translateY(-4px) rotate(30deg)"></div></div><div style="margin-left: auto;"> <div style=" width: 0px; border-top: 8px solid #F4F4F4; border-right: 8px solid transparent; transform: translateY(16px);"></div> <div style=" background-color: #F4F4F4; flex-grow: 0; height: 12px; width: 16px; transform: translateY(-4px);"></div> <div style=" width: 0; height: 0; border-top: 8px solid #F4F4F4; border-left: 8px solid transparent; transform: translateY(-4px) translateX(8px);"></div></div></div> <div style="display: flex; flex-direction: column; flex-grow: 1; justify-content: center; margin-bottom: 24px;"> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; margin-bottom: 6px; width: 224px;"></div> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; width: 144px;"></div></div></a></div></blockquote>`
}

/**
 * Whitelist + skeleton generator for social embeds. Returns null for any URL
 * that isn't a recognized X/Twitter, Instagram, or TikTok post.
 */
const parseExternalUrl = (
  raw: string,
): { provider: 'twitter' | 'x' | 'instagram' | 'tiktok'; html: string } | null => {
  const trimmed = raw.trim()
  if (!trimmed) return null

  let u: URL
  try {
    u = new URL(trimmed)
  } catch {
    return null
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null

  const host = u.hostname.replace(/^www\./, '').toLowerCase()

  if (
    (host === 'twitter.com' || host === 'x.com' || host === 'mobile.twitter.com')
    && /^\/[^/]+\/status\/\d+/.test(u.pathname)
  ) {
    const canonical = `https://twitter.com${u.pathname.replace(/\/$/, '')}`
    return {
      provider: host === 'x.com' ? 'x' : 'twitter',
      html: `<blockquote class="twitter-tweet" data-dnt="true"><a href="${escapeHtmlAttr(canonical)}"></a></blockquote>`,
    }
  }

  if (host === 'instagram.com' && /^\/(p|reel|tv)\/[^/]+\/?/.test(u.pathname)) {
    const cleanPath = u.pathname.endsWith('/') ? u.pathname : `${u.pathname}/`
    // Match the utm params Instagram's "Copy embed code" button appends —
    // their widget script keys off them as part of the embed-vs-direct hint.
    const permalink = new URL(`https://www.instagram.com${cleanPath}`)
    permalink.searchParams.set('utm_source', 'ig_embed')
    permalink.searchParams.set('utm_campaign', 'loading')
    return {
      provider: 'instagram',
      html: buildInstagramHtml(permalink.toString()),
    }
  }

  const tiktokMatch = u.pathname.match(/^\/@([^/]+)\/video\/(\d+)/)
  if (host === 'tiktok.com' && tiktokMatch) {
    const canonical = `https://www.tiktok.com${u.pathname.replace(/\/$/, '')}`
    const videoId = tiktokMatch[2]
    const safeUrl = escapeHtmlAttr(canonical)
    return {
      provider: 'tiktok',
      html: `<blockquote class="tiktok-embed" cite="${safeUrl}" data-video-id="${escapeHtmlAttr(videoId)}" style="max-width: 605px;min-width: 325px;"><section></section></blockquote>`,
    }
  }

  return null
}

const isMarkdownPart = (part: ArticlePart): part is MarkdownPart =>
  part._type === 'markdown_part'

const isPhotoPart = (part: ArticlePart): part is PhotoPart =>
  part._type === 'photo_part'

const isExternalPart = (part: ArticlePart): part is ExternalPart =>
  part._type === 'external_part'

const isVideoEmbedPart = (part: ArticlePart): part is VideoEmbedPart =>
  part._type === 'video_embed_part'

const partLabel = (type: string): string => {
  switch (type) {
    case 'markdown_part':
      return 'Markdown'
    case 'photo_part':
      return 'Photo'
    case 'external_part':
      return 'External embed'
    case 'video_embed_part':
      return 'Video embed'
    default:
      return type
  }
}

interface PhotoMetadataDraft {
  alt_text: string
  credit: string
}

interface PhotoPartEditorProps {
  image: ResolvedImage | undefined
  imageId: string
  metadata: PhotoMetadataDraft
  onUpload: (file: File) => Promise<void>
  onMetadataChange: (field: keyof PhotoMetadataDraft, value: string) => void
}

const PhotoPartEditor: React.FC<PhotoPartEditorProps> = ({
  image,
  imageId,
  metadata,
  onUpload,
  onMetadataChange,
}) => {
  const altInputId = `photo-alt-${imageId}`
  const creditInputId = `photo-credit-${imageId}`
  return (
    <Panel padding="compact" className={styles.photoPanel}>
      <ImageSlot
        image={image?.cdn_url ? {
          url: image.cdn_url,
          width: image.pixel_width,
          height: image.pixel_height,
        } : null}
        onUpload={onUpload}
        hint={!image?.cdn_url ? `Image not found (${imageId})` : undefined}
      />
      <div className={styles.photoMetadataFields}>
        <div className={formStyles.fieldGroup}>
          <label htmlFor={altInputId}>Alt text</label>
          <input
            id={altInputId}
            type="text"
            value={metadata.alt_text}
            onChange={(e) => onMetadataChange('alt_text', e.target.value)}
            placeholder="Describe the photo for screen readers"
          />
        </div>
        <div className={formStyles.fieldGroup}>
          <label htmlFor={creditInputId}>Credit</label>
          <input
            id={creditInputId}
            type="text"
            value={metadata.credit}
            onChange={(e) => onMetadataChange('credit', e.target.value)}
            placeholder="Photographer or agency"
          />
        </div>
      </div>
    </Panel>
  )
}

interface ExternalPartEditorProps {
  url: string
  provider: string | null
  onChange: (url: string) => void
}

const ExternalPartEditor: React.FC<ExternalPartEditorProps> = ({ url, provider, onChange }) => {
  const trimmed = url.trim()
  const hasInput = trimmed.length > 0
  const isInvalid = hasInput && provider === null
  return (
    <Panel padding="compact">
      <div className={formStyles.fieldGroup}>
        <label>Embed URL</label>
        <input
          type="url"
          value={url}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste an X, Instagram, or TikTok post URL"
          className={isInvalid ? styles.embedInputInvalid : undefined}
        />
        {isInvalid && (
          <p className={styles.embedError}>
            Unsupported URL. Only X/Twitter, Instagram, and TikTok are supported.
          </p>
        )}
        {provider && (
          <p className={styles.embedProvider}>
            Provider: <strong>{provider}</strong>
          </p>
        )}
      </div>
    </Panel>
  )
}

const ArticleDetail: React.FC = () => {
  const { articleId } = useParams<{ articleId: string }>()
  const confirm = useSpeedbump()
  const { toast } = useToast()
  const [title, setTitle] = useState('')
  const [headline, setHeadline] = useState('')
  const [byline, setByline] = useState('')
  const [stage, setStage] = useState<ArticleStage>('draft')
  const [parts, setParts] = useState<ArticlePart[]>([])
  const [partKeys, setPartKeys] = useState<string[]>([])
  const [resolvedImages, setResolvedImages] = useState<Record<string, ResolvedImage>>({})
  const [photoMetadata, setPhotoMetadata] = useState<Record<string, PhotoMetadataDraft>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [isAddingImage, setIsAddingImage] = useState(false)
  const addImageInputRef = useRef<HTMLInputElement | null>(null)
  // Track which article ID the current partKeys were generated for. We only
  // regenerate on article-id change; saves keep the same keys so the
  // uncontrolled MarkdownEditors don't remount and lose cursor/history.
  const partKeysArticleIdRef = useRef<string | null>(null)

  const { data: article, isLoading, error, mutate } = useSWR(
    articleId ? ['article', articleId] : null,
    () => fetchArticle(articleId!),
    { revalidateOnFocus: false },
  )

  const hydrateFromArticle = useCallback((a: ArticleDetailType) => {
    setTitle(a.title || '')
    setHeadline(a.headline || '')
    setByline(a.byline || '')
    setStage(a.stage)
    setParts(a.parts)
    if (partKeysArticleIdRef.current !== a.tc_article_id) {
      setPartKeys(a.parts.map(() => crypto.randomUUID()))
      partKeysArticleIdRef.current = a.tc_article_id
    }
    setResolvedImages(a.resolved_images)
    setPhotoMetadata(
      Object.fromEntries(
        Object.entries(a.resolved_images).map(([id, img]) => [
          id,
          { alt_text: img.alt_text ?? '', credit: img.credit ?? '' },
        ]),
      ),
    )
    setDirty(false)
    setSaveError(null)
  }, [])

  useEffect(() => {
    if (article) hydrateFromArticle(article)
  }, [article, hydrateFromArticle])

  const updateMarkdownPart = useCallback((index: number, content: string) => {
    setParts((prev) => {
      const next = [...prev]
      const part = next[index]
      if (part && part._type === 'markdown_part') {
        next[index] = { ...part, content }
      }
      return next
    })
    setDirty(true)
  }, [])

  // parts and partKeys move in lockstep so the uncontrolled MarkdownEditors
  // keep their key (and thus cursor/history) across a reorder.
  const { dragIndex, handleProps, rowProps } = useDragReorder(
    useCallback((from: number, to: number) => {
      setParts((prev) => arrayMove(prev, from, to))
      setPartKeys((prev) => arrayMove(prev, from, to))
      setDirty(true)
    }, []),
    { dragImageFromParent: true },
  )

  const handleDeletePart = useCallback(
    async (index: number) => {
      const part = parts[index]
      if (!part) return
      const ok = await confirm({
        title: 'Delete part',
        confirmLabel: 'Delete',
        confirmVariant: 'danger',
        message: `Delete this ${partLabel(part._type).toLowerCase()} part? This cannot be undone.`,
      })
      if (!ok) return
      setParts((prev) => prev.filter((_, i) => i !== index))
      setPartKeys((prev) => prev.filter((_, i) => i !== index))
      setDirty(true)
    },
    [parts, confirm],
  )

  const handleAddMarkdown = useCallback(() => {
    setParts((prev) => [...prev, { _type: 'markdown_part', content: '' }])
    setPartKeys((prev) => [...prev, crypto.randomUUID()])
    setDirty(true)
  }, [])

  const handleAddEmbed = useCallback(() => {
    setParts((prev) => [
      ...prev,
      { _type: 'external_part', url: '', provider: null, html: null },
    ])
    setPartKeys((prev) => [...prev, crypto.randomUUID()])
    setDirty(true)
  }, [])

  const handleAddVideo = useCallback(() => {
    setParts((prev) => [
      ...prev,
      {
        _type: 'video_embed_part',
        video_id: '',
        player_id: JWPLAYER_DEFAULT_PLAYER_ID,
      },
    ])
    setPartKeys((prev) => [...prev, crypto.randomUUID()])
    setDirty(true)
  }, [])

  const updateVideoPart = useCallback(
    (index: number, metadata: VideoEmbedMetadata) => {
      setParts((prev) => {
        const next = [...prev]
        const part = next[index]
        if (part && part._type === 'video_embed_part') {
          next[index] = {
            _type: 'video_embed_part',
            video_id: metadata.video_id ?? '',
            player_id: metadata.player_id ?? '',
            title: metadata.title ?? null,
            description: metadata.description ?? null,
            duration: metadata.duration ?? null,
          }
        }
        return next
      })
      setDirty(true)
    },
    [],
  )

  const updateExternalPart = useCallback((index: number, url: string) => {
    const parsed = parseExternalUrl(url)
    setParts((prev) => {
      const next = [...prev]
      const part = next[index]
      if (part && part._type === 'external_part') {
        next[index] = {
          ...part,
          url,
          provider: parsed?.provider ?? null,
          html: parsed?.html ?? null,
        }
      }
      return next
    })
    setDirty(true)
  }, [])

  const handleAddImageClick = useCallback(() => {
    addImageInputRef.current?.click()
  }, [])

  const handleAddImageFile = useCallback(
    async (file: File) => {
      if (!articleId) return
      setIsAddingImage(true)
      try {
        const uploaded = await uploadArticleImage(articleId, file)
        setResolvedImages((prev) => ({ ...prev, [uploaded.tc_image_id]: uploaded }))
        setPhotoMetadata((prev) => ({
          ...prev,
          [uploaded.tc_image_id]: {
            alt_text: uploaded.alt_text ?? '',
            credit: uploaded.credit ?? '',
          },
        }))
        setParts((prev) => [
          ...prev,
          { _type: 'photo_part', tc_image_id: uploaded.tc_image_id },
        ])
        setPartKeys((prev) => [...prev, crypto.randomUUID()])
        setDirty(true)
      } catch (err) {
        setSaveError((err as Error).message || 'Failed to upload image')
      } finally {
        setIsAddingImage(false)
      }
    },
    [articleId],
  )

  const handleUploadPhoto = useCallback(
    async (index: number, file: File) => {
      if (!articleId) return
      const uploaded = await uploadArticleImage(articleId, file)
      setResolvedImages((prev) => ({ ...prev, [uploaded.tc_image_id]: uploaded }))
      setPhotoMetadata((prev) => ({
        ...prev,
        [uploaded.tc_image_id]: {
          alt_text: uploaded.alt_text ?? '',
          credit: uploaded.credit ?? '',
        },
      }))
      setParts((prev) => {
        const next = [...prev]
        const part = next[index]
        if (part && part._type === 'photo_part') {
          next[index] = { ...part, tc_image_id: uploaded.tc_image_id }
        }
        return next
      })
      setDirty(true)
    },
    [articleId],
  )

  const handlePhotoMetadataChange = useCallback(
    (imageId: string, field: keyof PhotoMetadataDraft, value: string) => {
      setPhotoMetadata((prev) => ({
        ...prev,
        [imageId]: {
          alt_text: prev[imageId]?.alt_text ?? '',
          credit: prev[imageId]?.credit ?? '',
          [field]: value,
        },
      }))
      setDirty(true)
    },
    [],
  )

  const updateField = useCallback(
    (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
      setter(value)
      setDirty(true)
    },
    [],
  )

  const handleStageChange = useCallback((next: ArticleStage) => {
    setStage(next)
    setDirty(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (!articleId) return
    if (!title.trim()) {
      setSaveError('Title is required')
      return
    }

    const photoMetadataPayload: Record<string, PhotoImageMetadataUpdate> = {}
    const saved = article?.resolved_images ?? {}
    for (const part of parts) {
      if (!isPhotoPart(part)) continue
      const draft = photoMetadata[part.tc_image_id]
      if (!draft) continue
      const original = saved[part.tc_image_id]
      const origAlt = original?.alt_text ?? ''
      const origCredit = original?.credit ?? ''
      const changes: PhotoImageMetadataUpdate = {}
      if (draft.alt_text !== origAlt) {
        changes.alt_text = draft.alt_text.trim() ? draft.alt_text : null
      }
      if (draft.credit !== origCredit) {
        changes.credit = draft.credit.trim() ? draft.credit : null
      }
      if (Object.keys(changes).length > 0) {
        photoMetadataPayload[part.tc_image_id] = changes
      }
    }

    setIsSaving(true)
    setSaveError(null)
    try {
      await updateArticle(articleId, {
        title: title.trim(),
        headline: headline.trim() || null,
        byline: byline.trim() || null,
        parts,
        ...(stage !== article?.stage ? { stage } : {}),
        ...(Object.keys(photoMetadataPayload).length > 0
          ? { photo_image_metadata: photoMetadataPayload }
          : {}),
      })
      await mutate()
      setDirty(false)
      toast('Article saved')
    } catch (err) {
      setSaveError((err as Error).message || 'Failed to save changes')
    } finally {
      setIsSaving(false)
    }
  }, [articleId, title, headline, byline, stage, parts, photoMetadata, article, mutate, toast])

  const handleCancel = useCallback(() => {
    if (article) hydrateFromArticle(article)
  }, [article, hydrateFromArticle])

  const editableCount = useMemo(
    () => parts.filter((p) => isMarkdownPart(p) || isPhotoPart(p)).length,
    [parts],
  )

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading article</h2>
          <p>{(error as Error).message}</p>
          <Button variant="secondary" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  if (isLoading || !article) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading article...</h3>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <Link to="/content/articles" className={styles.backLink}>
        ← Back to articles
      </Link>

      <div className={pageStyles.pageHeader}>
        <h1>{title || 'Untitled article'}</h1>
        <p>
          <code className="id-code">{article.source}/{article.source_id}</code>
          {' '}
          <Badge variant={stage === 'published' ? 'success' : 'warning'}>
            {stage}
          </Badge>
          {article.published_at && (
            <>
              {' '}
              <FormattedDateTime dateString={article.published_at} />
            </>
          )}
        </p>
      </div>

      <Panel>
        <Panel.Header>
          <h2>Article Details</h2>
        </Panel.Header>
        <div className={formStyles.formField}>
          <div className={formStyles.fieldGroup}>
            <label htmlFor="article-title">Title</label>
            <input
              id="article-title"
              type="text"
              value={title}
              onChange={(e) => updateField(setTitle, e.target.value)}
              placeholder="Article title"
            />
          </div>

          <div className={formStyles.fieldGroup}>
            <label htmlFor="article-headline">Headline</label>
            <input
              id="article-headline"
              type="text"
              value={headline}
              onChange={(e) => updateField(setHeadline, e.target.value)}
              placeholder="Optional headline"
            />
          </div>

          <div className={formStyles.fieldGroup}>
            <label htmlFor="article-byline">Byline</label>
            <input
              id="article-byline"
              type="text"
              value={byline}
              onChange={(e) => updateField(setByline, e.target.value)}
              placeholder="Optional byline"
            />
          </div>

          <div className={formStyles.fieldGroup}>
            <label>Stage</label>
            <ToggleGroup
              variant="toggle"
              size="small"
              options={STAGE_OPTIONS}
              value={stage}
              onChange={handleStageChange}
            />
          </div>
        </div>
      </Panel>

      {articleId && (
        <SlugManager entityType="article" entityId={articleId} />
      )}

      {parts.length === 0 && (
        <p className={styles.readOnlyHint}>
          No body parts for this article in the {article.language_code || 'requested'} translation.
        </p>
      )}
      {parts.length > 0 && (
        <>
          <div className={styles.partsList}>
            {parts.map((part, index) => (
              <div
                key={partKeys[index] ?? index}
                className={`${styles.partRow} ${dragIndex === index ? styles.partRowDragging : ''}`}
                {...rowProps(index)}
              >
                <span
                  className={styles.dragHandle}
                  aria-label="Drag to reorder"
                  {...handleProps(index)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className={styles.gripIcon} aria-hidden="true">
                    <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
                    <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
                    <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
                  </svg>
                </span>
                <div className={styles.partContent}>
                  {isMarkdownPart(part) ? (
                    <MarkdownEditor
                      initialValue={part.content || ''}
                      onChange={(md) => updateMarkdownPart(index, md)}
                      placeholder="Write markdown..."
                      ariaLabel={`Markdown part ${index + 1}`}
                    />
                  ) : isPhotoPart(part) ? (
                    <PhotoPartEditor
                      image={resolvedImages[part.tc_image_id]}
                      imageId={part.tc_image_id}
                      metadata={
                        photoMetadata[part.tc_image_id] ?? { alt_text: '', credit: '' }
                      }
                      onUpload={(file) => handleUploadPhoto(index, file)}
                      onMetadataChange={(field, value) =>
                        handlePhotoMetadataChange(part.tc_image_id, field, value)
                      }
                    />
                  ) : isExternalPart(part) ? (
                    <ExternalPartEditor
                      url={part.url || ''}
                      provider={part.provider || null}
                      onChange={(url) => updateExternalPart(index, url)}
                    />
                  ) : isVideoEmbedPart(part) ? (
                    <Panel padding="compact">
                      <VideoEmbedEditor
                        metadata={{
                          video_id: part.video_id,
                          player_id: part.player_id,
                          title: part.title ?? undefined,
                          description: part.description ?? undefined,
                          duration: part.duration ?? null,
                        }}
                        onChange={(metadata) => updateVideoPart(index, metadata)}
                      />
                    </Panel>
                  ) : (
                    <>
                      <div className={styles.partTypeLabel}>{partLabel(part._type)}</div>
                      <pre className={styles.readOnlyPart}>
                        {JSON.stringify(part, null, 2)}
                      </pre>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.deleteButton}
                  onClick={() => handleDeletePart(index)}
                  aria-label="Delete part"
                  title="Delete part"
                >
                  <TrashIcon size={14} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className={styles.addButtonsRow}>
        <Button variant="secondary" size="small" onClick={handleAddMarkdown}>
          + Add markdown
        </Button>
        <Button
          variant="secondary"
          size="small"
          onClick={handleAddImageClick}
          disabled={isAddingImage}
        >
          {isAddingImage ? 'Uploading…' : '+ Add image'}
        </Button>
        <Button variant="secondary" size="small" onClick={handleAddEmbed}>
          + Add embed
        </Button>
        <Button variant="secondary" size="small" onClick={handleAddVideo}>
          + Add video
        </Button>
        <input
          ref={addImageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void handleAddImageFile(f)
            if (addImageInputRef.current) addImageInputRef.current.value = ''
          }}
        />
      </div>

      {saveError && (
        <Banner
          variant="error"
          message={saveError}
          onDismiss={() => setSaveError(null)}
        />
      )}

      <div className={styles.actionBar}>
        <Button variant="secondary" onClick={handleCancel} disabled={!dirty || isSaving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!dirty || isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  )
}

export default ArticleDetail
